import React, { useState } from 'react';
import { BookOpen, Download, ExternalLink, Search, Filter, FileText, Play, Headphones, Book, Globe, Bookmark, ChevronRight } from '../components/CustomIcons';

const RESOURCES = [
  {
    id: 'r1', category: 'guides', type: 'pdf',
    title: 'Reentry Resource Guide — Texas', desc: 'Comprehensive guide covering housing, employment, benefits, and legal resources in Texas. Updated 2025.',
    source: 'Texas Department of Criminal Justice', featured: true
  },
  {
    id: 'r2', category: 'guides', type: 'pdf',
    title: 'Federal Benefits After Release', desc: 'Social Security, SNAP, Medicaid eligibility and application process for returning citizens.',
    source: 'U.S. Department of Justice'
  },
  {
    id: 'r3', category: 'education', type: 'guide',
    title: 'GED Study Guide (2024 Edition)', desc: 'Complete study materials for all 4 GED test subjects: Math, Science, Social Studies, Reasoning Through Language Arts.',
    source: 'GED Testing Service', featured: true
  },
  {
    id: 'r4', category: 'education', type: 'guide',
    title: 'CompTIA A+ Study Notes', desc: 'Core 1 and Core 2 exam prep notes, practice questions, and mnemonics for IT fundamentals.',
    source: 'Assistiv OS Instructors'
  },
  {
    id: 'r5', category: 'legal', type: 'guide',
    title: 'Know Your Rights: Parole & Probation', desc: 'Plain-language overview of your rights and responsibilities while on community supervision.',
    source: 'ACLU'
  },
  {
    id: 'r6', category: 'legal', type: 'pdf',
    title: 'Expungement & Record Sealing Guide', desc: 'How to petition for expungement or nondisclosure of criminal records by state.',
    source: 'Legal Aid Society'
  },
  {
    id: 'r7', category: 'employment', type: 'guide',
    title: 'Second-Chance Hiring: Employer List', desc: 'Verified list of 500+ employers with "ban the box" policies and second-chance hiring programs.',
    source: 'Clean Slate Initiative', featured: true
  },
  {
    id: 'r8', category: 'employment', type: 'guide',
    title: 'Resume Writing for Returning Citizens', desc: 'Step-by-step guide to crafting a resume that highlights skills and handles the employment gap.',
    source: 'Assistiv OS Career Services'
  },
  {
    id: 'r9', category: 'financial', type: 'guide',
    title: 'Banking After Incarceration', desc: 'How to open a second-chance bank account, build credit, and avoid predatory financial products.',
    source: 'Cities for Financial Empowerment'
  },
  {
    id: 'r10', category: 'health', type: 'guide',
    title: 'Mental Health Resources: Reentry Toolkit', desc: 'Coping strategies, community mental health centers, and crisis resources for returning citizens.',
    source: 'SAMHSA'
  },
  {
    id: 'r11', category: 'education', type: 'video',
    title: 'Introduction to Electrical Theory (Video Series)', desc: '12-part video series covering Ohm\'s Law, circuits, and safety basics. Great supplement to coursework.',
    source: 'Assistiv OS Media', duration: '3h 40min'
  },
  {
    id: 'r12', category: 'health', type: 'audio',
    title: 'Mindfulness for Stress Reduction (Audio)', desc: '8-week guided mindfulness program with daily 15-minute sessions. Clinically validated.',
    source: 'Wellbeing Works', duration: '12 sessions'
  },
];

const CATEGORIES = [
  { value: 'all', label: 'All Resources' },
  { value: 'guides', label: 'Reentry Guides' },
  { value: 'education', label: 'Education' },
  { value: 'legal', label: 'Legal' },
  { value: 'employment', label: 'Employment' },
  { value: 'financial', label: 'Financial' },
  { value: 'health', label: 'Health' },
];

const TYPE_ICONS = {
  pdf: FileText,
  guide: BookOpen,
  video: Play,
  audio: Headphones,
};

function ResourceCard({ resource, bookmarked, onBookmark }) {
  const Icon = TYPE_ICONS[resource.type] || FileText;
  return (
    <div style={{ background: 'var(--bg-surface)', border: `1px solid ${resource.featured ? 'rgba(232,119,34,0.3)' : 'var(--border)'}`, borderRadius: '14px', padding: '20px', display: 'flex', gap: '14px' }}
      data-testid={`resource-${resource.id}`}>
      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: resource.featured ? 'var(--orange-subtle)' : 'var(--surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={resource.featured ? '#F97316' : '#666666'} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', flex: 1, marginRight: '8px' }}>{resource.title}</div>
          {resource.featured && <span style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', flexShrink: 0 }}>FEATURED</span>}
        </div>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>{resource.desc}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
            <Globe size={11} style={{ display: 'inline', marginRight: '4px' }} />
            {resource.source}
            {resource.duration && <span style={{ marginLeft: '10px' }}>{resource.duration}</span>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => onBookmark(resource.id)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: bookmarked ? '#F97316' : '#444444', padding: '4px' }}>
              <Bookmark size={15} fill={bookmarked ? '#F97316' : 'none'} />
            </button>
            <button style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '7px', padding: '5px 12px', cursor: 'pointer', color: '#F97316', fontSize: '12px', fontWeight: 600 }}>
              <ExternalLink size={12} /> Access
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Library() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [bookmarks, setBookmarks] = useState(new Set());

  const filtered = RESOURCES.filter(r => {
    const matchCat = category === 'all' || r.category === category;
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const toggleBookmark = (id) => {
    setBookmarks(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const featured = filtered.filter(r => r.featured);
  const others = filtered.filter(r => !r.featured);

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="library-page">
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Library</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>Guides, study materials, legal resources, and wellness tools — all in one place.</p>
        </div>

        {/* Search & Filter */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search resources..."
              style={{ width: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '10px 12px 10px 38px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              data-testid="library-search"
            />
          </div>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '10px 16px', color: 'var(--text-primary)', fontSize: '14px', cursor: 'pointer' }}
            data-testid="library-filter"
          >
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {/* Stats Bar */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[{ label: 'Total Resources', value: RESOURCES.length }, { label: 'Featured', value: RESOURCES.filter(r => r.featured).length }, { label: 'Bookmarked', value: bookmarks.size }].map(s => (
            <div key={s.label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: '#F97316' }}>{s.value}</span>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#F97316', letterSpacing: '1px', marginBottom: '12px' }}>FEATURED</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              {featured.map(r => <ResourceCard key={r.id} resource={r} bookmarked={bookmarks.has(r.id)} onBookmark={toggleBookmark} />)}
            </div>
          </>
        )}

        {/* All Results */}
        {others.length > 0 && (
          <>
            {featured.length > 0 && <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '1px', marginBottom: '12px' }}>ALL RESOURCES</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {others.map(r => <ResourceCard key={r.id} resource={r} bookmarked={bookmarks.has(r.id)} onBookmark={toggleBookmark} />)}
            </div>
          </>
        )}

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px', color: 'var(--text-dim)' }}>
            <BookOpen size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
            <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>No resources match your search.</p>
            <p style={{ fontSize: '13px' }}>Try a different keyword or category.</p>
          </div>
        )}
      </div>
    </div>
  );
}
